export interface ArtistCandidate {
    id: string;
    name: string;
}

export interface CatalogGroupedResult {
    catalog?: unknown;
}

export interface CatalogReleaseReference {
    id: string;
    title: string;
}

export interface CatalogResultPartition<T> {
    catalog: T[];
    other: T[];
}

export type CommandSearchDestination = 'albums' | 'artists' | 'elsewhere' | 'loading' | 'songs';
export interface CommandSearchSummary {
    albums: number;
    artists: number;
    musyncConfigured: boolean;
    settled: boolean;
    songs: number;
}

export interface CoverReference {
    cover_fallback_url?: null | string;
    cover_url?: null | string;
}

export interface DownloadCandidate {
    accepted: boolean;
    directory: string;
    id: string;
    owner: string;
}

export interface DownloadRequest {
    result_ids: string[];
}

export type ElsewhereSearchViewAction<TLibraryMatch = unknown> =
    | { libraryMatch: null | TLibraryMatch; searchId: string; type: 'succeeded' }
    | { preserveCurrent: boolean; type: 'started' }
    | { type: 'failed' };

export interface ElsewhereSearchViewState<TLibraryMatch = unknown> {
    activeSearchId: string | undefined;
    libraryMatch: null | TLibraryMatch;
}

export const reduceElsewhereSearchView = <TLibraryMatch>(
    state: ElsewhereSearchViewState<TLibraryMatch>,
    action: ElsewhereSearchViewAction<TLibraryMatch>,
): ElsewhereSearchViewState<TLibraryMatch> => {
    if (action.type === 'started') {
        return action.preserveCurrent ? state : { activeSearchId: undefined, libraryMatch: null };
    }
    if (action.type === 'succeeded') {
        return { activeSearchId: action.searchId, libraryMatch: action.libraryMatch };
    }
    return state;
};

export type ElsewhereSourceKind = 'artist' | 'recording' | 'release';

export const INTERACTIVE_MAX_RESULTS = 1000;
export const INTERACTIVE_RESPONSE_LIMIT = 80;
export const DEEP_SEARCH_MAX_RESULTS = 3000;
export const DEEP_SEARCH_RESPONSE_LIMIT = 200;

const ELSEWHERE_TERMINAL_STATES = new Set([
    'cancelled',
    'completed',
    'empty',
    'failed',
    'succeeded',
    'timed_out',
    'timeout',
]);

export const isElsewhereSearchDrained = (searchState: null | string, hasMore: boolean): boolean =>
    Boolean(searchState && ELSEWHERE_TERMINAL_STATES.has(searchState) && !hasMore);

export const canStopElsewhereSearch = (searchState: null | string): boolean =>
    searchState === null || searchState === 'running';

export interface IdentifiedResult {
    id: string;
}

export type LibrarySearchItemType =
    | 'album'
    | 'albumArtist'
    | 'albumArtists'
    | 'albums'
    | 'song'
    | 'songs';

export interface LibrarySearchRequest {
    artist?: string;
    artist_mbid?: string;
    query: string;
    target_kind?: 'recording' | 'release';
    target_mbid?: string;
    title?: string;
}

export interface NamedResult {
    name: string;
}

export interface QualityCandidate {
    is_lossless: boolean;
}

export type ReleaseSortMode = 'relevance' | 'title-asc' | 'year-asc' | 'year-desc';

export interface SortableRelease {
    catalog?: { year?: null | number };
    key: string;
    title: string;
}

export interface SourceSelection {
    resultIds: string[];
    sourceKey: string;
}

const SOURCE_KIND_BY_ITEM: Record<LibrarySearchItemType, ElsewhereSourceKind> = {
    album: 'release',
    albumArtist: 'artist',
    albumArtists: 'artist',
    albums: 'release',
    song: 'recording',
    songs: 'recording',
};

export const normalizeReleaseTitle = (value: string): string =>
    value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\[[^\]]*\]|\([^)]*\)|\{[^}]*\}/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

const normalizeTrackName = (value: string): string => {
    const withoutExtension = value.replace(/\.[a-z0-9]{1,8}$/i, '');
    const withoutTrackNumber = withoutExtension.replace(/^\s*\d{1,3}\s*(?:[-._]\s*)?/, '');
    return normalizeReleaseTitle(withoutTrackNumber);
};

export const buildSourceKey = (owner: string, directory: string): string =>
    JSON.stringify([owner, directory]);

export const mergeExpandedResults = <T extends IdentifiedResult>(
    current: T[],
    expanded: T[],
): T[] => {
    const merged = [...current];
    const seen = new Set(current.map((item) => item.id));
    for (const item of expanded) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        merged.push(item);
    }
    return merged;
};

export const resolveStableReleaseKey = (
    sourceKey: string,
    fallbackKey: string,
    catalogId: string | undefined,
    sourceKeys: Map<string, string>,
    catalogKeys: Map<string, string>,
): string => {
    const existing = sourceKeys.get(sourceKey);
    if (existing) {
        if (catalogId && !catalogKeys.has(catalogId)) catalogKeys.set(catalogId, existing);
        return existing;
    }
    const key =
        (catalogId && catalogKeys.get(catalogId)) ||
        (catalogId ? `catalog:${catalogId}` : fallbackKey);
    sourceKeys.set(sourceKey, key);
    if (catalogId && !catalogKeys.has(catalogId)) catalogKeys.set(catalogId, key);
    return key;
};

const trackCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export const sortReleaseGroups = <T extends SortableRelease>(
    releases: T[],
    mode: ReleaseSortMode,
): T[] => {
    const sorted = [...releases];
    if (mode === 'relevance') return sorted;

    const compareTitle = (a: T, b: T) =>
        trackCollator.compare(a.title, b.title) || trackCollator.compare(a.key, b.key);
    if (mode === 'title-asc') return sorted.sort(compareTitle);

    return sorted.sort((a, b) => {
        const aYear = a.catalog?.year;
        const bYear = b.catalog?.year;
        if (aYear == null && bYear == null) return compareTitle(a, b);
        if (aYear == null) return 1;
        if (bYear == null) return -1;
        const yearOrder = mode === 'year-asc' ? aYear - bYear : bYear - aYear;
        return yearOrder || compareTitle(a, b);
    });
};

/*
 * `catalog.year` is the only year a release group ever has — peer folder names
 * are never parsed for one. Without catalog context every year is null, so the
 * comparator above falls straight through to `compareTitle` and both year modes
 * become a silent alias for `title-asc`: the menu would offer an order that
 * does not order. Drop them from the menu instead, and degrade any year mode
 * already held in state to `relevance` rather than to a lie.
 */
const YEAR_SORT_MODES: ReleaseSortMode[] = ['year-asc', 'year-desc'];

export const sortModeIsAvailable = (mode: ReleaseSortMode, hasCatalogContext: boolean): boolean =>
    hasCatalogContext || !YEAR_SORT_MODES.includes(mode);

export const resolveSortMode = (
    mode: ReleaseSortMode,
    hasCatalogContext: boolean,
): ReleaseSortMode => (sortModeIsAvailable(mode, hasCatalogContext) ? mode : 'relevance');

export const sortSourceTracks = <T extends NamedResult>(items: T[]): T[] =>
    [...items].sort((a, b) => trackCollator.compare(a.name, b.name));

export const buildReleaseGroupKey = (folder: string, trackNames: string[]): string => {
    const tracks = trackNames.map(normalizeTrackName).filter(Boolean).sort();
    return JSON.stringify(
        tracks.length > 1 ? ['tracks', tracks] : ['single', normalizeReleaseTitle(folder), tracks],
    );
};

const tokenSequenceIndex = (haystack: string[], needle: string[]): number => {
    if (needle.length === 0 || needle.length > haystack.length) return -1;
    for (let start = 0; start <= haystack.length - needle.length; start += 1) {
        if (needle.every((token, offset) => haystack[start + offset] === token)) return start;
    }
    return -1;
};

const sharedFolderSuffixTitlePrefix = (folder: string[], title: string[]): string[] => {
    const maximum = Math.min(folder.length, title.length);
    for (let length = maximum; length > 0; length -= 1) {
        const suffix = folder.slice(-length);
        if (suffix.every((token, index) => token === title[index])) return suffix;
    }
    return [];
};

const isMeaningfulReleaseMatch = (tokens: string[]): boolean =>
    tokens.length > 1 || (tokens.length === 1 && tokens[0].length >= 8);

export const findCatalogRelease = <T extends CatalogReleaseReference & CoverReference>(
    folder: string,
    releases: T[],
): T | undefined => {
    const folderKey = normalizeReleaseTitle(folder);
    if (folderKey.length < 4) return undefined;
    const folderTokens = folderKey.split(' ');

    return releases
        .map((release) => {
            const titleKey = normalizeReleaseTitle(release.title);
            const titleTokens = titleKey.split(' ').filter(Boolean);
            const exact = folderKey === titleKey;
            const completeTitleIndex = tokenSequenceIndex(folderTokens, titleTokens);
            const sharedPrefix = sharedFolderSuffixTitlePrefix(folderTokens, titleTokens);
            const matchedTokens = completeTitleIndex >= 0 ? titleTokens : sharedPrefix;
            if (!exact && !isMeaningfulReleaseMatch(matchedTokens)) return undefined;
            return {
                completeTitle: completeTitleIndex >= 0,
                exact,
                matchedTokenCount: matchedTokens.length,
                release,
                titleLength: titleKey.length,
            };
        })
        .filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate))
        .sort((a, b) => {
            const exactDifference = Number(b.exact) - Number(a.exact);
            if (exactDifference !== 0) return exactDifference;
            const tokenDifference = b.matchedTokenCount - a.matchedTokenCount;
            if (tokenDifference !== 0) return tokenDifference;
            const completeDifference = Number(b.completeTitle) - Number(a.completeTitle);
            if (completeDifference !== 0) return completeDifference;
            const lengthDifference = b.titleLength - a.titleLength;
            if (lengthDifference !== 0) return lengthDifference;
            return (
                Number(Boolean(b.release.cover_fallback_url)) -
                Number(Boolean(a.release.cover_fallback_url))
            );
        })[0]?.release;
};

export const findCatalogReleaseForSource = <T extends CatalogReleaseReference & CoverReference>(
    directory: string,
    folder: string,
    releases: T[],
): T | undefined => {
    const segments = directory.split(/[\\/]+/).filter(Boolean);
    if (normalizeReleaseTitle(segments.at(-1) || '') === normalizeReleaseTitle(folder)) {
        segments.pop();
    }
    for (const segment of segments.slice(-2).reverse()) {
        const parentMatch = findCatalogRelease(segment, releases);
        if (parentMatch) return parentMatch;
    }
    return findCatalogRelease(folder, releases);
};

export const preferredCoverUrl = (cover: CoverReference | undefined): string | undefined =>
    cover?.cover_fallback_url || cover?.cover_url || undefined;

export const nextVisibleResultCount = (current: number, total: number): number =>
    Math.min(total, current * 2);

export const partitionCatalogResults = <T extends CatalogGroupedResult>(
    groups: T[],
    hasCatalogContext: boolean,
): CatalogResultPartition<T> => {
    if (!hasCatalogContext) return { catalog: groups, other: [] };
    return {
        catalog: groups.filter((group) => Boolean(group.catalog)),
        other: groups.filter((group) => !group.catalog),
    };
};

export const resolveArtistCandidate = <T extends ArtistCandidate>(
    query: string,
    candidates: T[],
): T | undefined => {
    const wanted = normalizeReleaseTitle(query);
    if (wanted.length < 3) return undefined;

    const exact = candidates.filter(
        (candidate) => normalizeReleaseTitle(candidate.name) === wanted,
    );
    if (exact.length === 1) return exact[0];

    const leading = candidates.filter((candidate) => {
        const name = normalizeReleaseTitle(candidate.name);
        return name.startsWith(`${wanted} `) || wanted.startsWith(`${name} `);
    });
    return leading.length === 1 ? leading[0] : undefined;
};

/*
 * The catalog identity is what supplies release year, type, artwork, the year
 * sorts and the Owned badge - the screen is crippled without it, so a
 * confident artist match is applied automatically in *every* scope; there is
 * exactly one way this screen looks, not a bare state and an enriched one.
 *
 * What stays scope-dependent is the partition below: in an artist-shaped
 * search, folders outside the discography are demoted behind a disclosure; in
 * a track- or release-shaped search the artist is a guess, so every peer
 * folder stays visible and the catalog only ever *adds* metadata.
 */
export const shouldPartitionByCatalog = (scope: 'all' | ElsewhereSourceKind): boolean =>
    scope === 'all' || scope === 'artist';

export const resolveArtistChoices = <T extends ArtistCandidate>({
    candidates,
    dismissed,
    query,
    selected,
}: {
    candidates: T[];
    dismissed: boolean;
    query: string;
    selected: boolean;
}): T[] => {
    if (selected) return [];
    const unique = resolveArtistCandidate(query, candidates);
    // The route applies `unique` itself, so only ambiguity needs a picker -
    // and "Change artist" (a dismissal) reopens the manual choice.
    return dismissed || !unique ? candidates : [];
};

export const sourceKindForLibraryItem = (itemType: LibrarySearchItemType): ElsewhereSourceKind =>
    SOURCE_KIND_BY_ITEM[itemType];

export const sourceMatchesQuality = (items: QualityCandidate[], showLossy: boolean): boolean =>
    showLossy || items.some((item) => item.is_lossless);

export const shouldAutoExpandSource = (isLive: boolean, matchingTrackCount: number): boolean =>
    !isLive && matchingTrackCount > 0 && matchingTrackCount <= 2;

export const resolveCommandSearchDestination = ({
    albums,
    artists,
    musyncConfigured,
    settled,
    songs,
}: CommandSearchSummary): CommandSearchDestination => {
    if (!settled) return 'loading';
    if (albums + artists + songs === 0) return musyncConfigured ? 'elsewhere' : 'songs';
    if (songs > 0) return 'songs';
    if (albums > 0) return 'albums';
    return 'artists';
};

export const buildElsewhereSearchParams = (query: string, source?: ElsewhereSourceKind): string => {
    const params = new URLSearchParams({ autoStart: '1', query: query.trim() });
    if (source) params.set('source', source);
    return params.toString();
};

export const activateSource = (
    current: SourceSelection | undefined,
    sourceKey: string,
): SourceSelection => (current?.sourceKey === sourceKey ? current : { resultIds: [], sourceKey });

export const selectSourceTracks = (sourceKey: string, resultIds: string[]): SourceSelection => ({
    resultIds: [...new Set(resultIds)],
    sourceKey,
});

export const toggleSourceTrack = (
    current: SourceSelection | undefined,
    sourceKey: string,
    resultId: string,
    checked: boolean,
): SourceSelection => {
    const active = activateSource(current, sourceKey);
    const resultIds = new Set(active.resultIds);
    if (checked) resultIds.add(resultId);
    else resultIds.delete(resultId);
    return { resultIds: [...resultIds], sourceKey };
};

/*
 * Every release is an independent download: what the user checks in one album
 * must never move, clear or hide what is checked in another. Selection is
 * therefore keyed by release, and opening or closing a release panel is pure
 * disclosure - it never touches the selection map.
 */
export type ReleaseSelections = Record<string, SourceSelection>;

export const setReleaseSelection = (
    current: ReleaseSelections,
    releaseKey: string,
    selection: SourceSelection,
): ReleaseSelections => ({ ...current, [releaseKey]: selection });

/** Drop ids that are no longer visible (for example after a quality filter
 * change) so a stale selection degrades instead of blocking the download. */
export const pruneSelection = (
    selection: SourceSelection,
    visibleIds: Iterable<string>,
): SourceSelection => {
    const visible = new Set(visibleIds);
    const resultIds = selection.resultIds.filter((id) => visible.has(id));
    return resultIds.length === selection.resultIds.length
        ? selection
        : { resultIds, sourceKey: selection.sourceKey };
};

export const selectionSignature = (selection: SourceSelection): string =>
    `${selection.sourceKey}|${[...selection.resultIds].sort().join(':')}`;

export interface PendingReleaseSelection {
    releaseKey: string;
    selection: SourceSelection;
}

/** Selections that still have something to send: non-empty and not already
 * submitted with exactly this set of tracks. */
export const pendingReleaseSelections = (
    selections: ReleaseSelections,
    submitted: Record<string, string>,
): PendingReleaseSelection[] =>
    Object.entries(selections)
        .filter(
            ([releaseKey, selection]) =>
                selection.resultIds.length > 0 &&
                submitted[releaseKey] !== selectionSignature(selection),
        )
        .map(([releaseKey, selection]) => ({ releaseKey, selection }));

export const buildLibrarySearchRequest = (
    query: string,
    sourceKind: ElsewhereSourceKind,
): LibrarySearchRequest => {
    const cleaned = query.trim();
    if (sourceKind === 'artist') {
        return { artist: cleaned, query: cleaned, target_kind: 'release' };
    }
    return { query: cleaned, target_kind: sourceKind, title: cleaned };
};

export const buildCurrentSearchRequest = ({
    artist,
    artistMbid,
    query,
    scope,
    targetMbid,
}: {
    artist: string | undefined;
    artistMbid: string | undefined;
    query: string;
    scope: 'all' | ElsewhereSourceKind;
    targetMbid?: string;
}): LibrarySearchRequest => {
    const request =
        scope === 'all' ? { query: query.trim() } : buildLibrarySearchRequest(query, scope);
    const cleanedArtist = artist?.trim();

    return {
        ...request,
        ...(artistMbid && cleanedArtist && !request.artist ? { artist: cleanedArtist } : {}),
        ...(artistMbid ? { artist_mbid: artistMbid } : {}),
        ...(targetMbid && request.target_kind ? { target_mbid: targetMbid } : {}),
    };
};

export const buildDownloadRequest = (items: DownloadCandidate[]): DownloadRequest | undefined => {
    const downloadable = items.filter((item) => item.accepted);
    if (downloadable.length === 0) return undefined;

    const [{ directory, owner }] = downloadable;
    if (downloadable.some((item) => item.owner !== owner || item.directory !== directory)) {
        throw new Error('Choose files from one peer folder per download.');
    }

    return { result_ids: downloadable.map((item) => item.id) };
};

export const buildSelectionDownloadRequest = (
    items: DownloadCandidate[],
    selection: SourceSelection,
): DownloadRequest | undefined => {
    const selectedIds = new Set(selection.resultIds);
    const selected = items.filter((item) => selectedIds.has(item.id));
    if (selected.length !== selectedIds.size) {
        throw new Error('Selection contains unavailable tracks.');
    }
    if (
        selected.some((item) => buildSourceKey(item.owner, item.directory) !== selection.sourceKey)
    ) {
        throw new Error('Selection contains a track outside the active source.');
    }
    return buildDownloadRequest(selected);
};
